import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router";

import { useApi } from "@/api/hooks/useApi";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { CommunityPoll } from "@/components/CommunityFeed/CommunityPoll";
import { getCommunityPostDocumentTitle } from "@/components/CommunityFeed/getCommunityPostDocumentTitle";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Redirect } from "@/components/Routing/Redirect";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import type { MessageRouteStates } from "@/helpers/community-feed";
import { isHttpError } from "@/helpers/Network/errors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSlug } from "@/hooks/useSlug";
import { CommunityPostViewCountTrackingProvider } from "@/providers/CommunityPostViewCountTrackingProvider";
import { usePollQueries } from "@/queries/polls/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function PollDetailsPage(): React.ReactNode {
  const projectId = useProjectId();
  const slug = useSlug();
  const { t } = useTranslation();

  const api = useApi();
  const { id: pollId } = useParams<{ id: string }>();

  const pollQueries = usePollQueries();

  const {
    data: pollDetails,
    isPending: isPendingPollDetails,
    error: errorPollDetails,
  } = useQuery({
    ...pollQueries.details(pollId!),
    enabled: !!pollId,
  });
  const isUnread = pollDetails?.isUnread === true;

  useEffect(() => {
    if (isUnread && pollId) {
      void api.postPollsReadV1(pollId);
      if (pollDetails.messageId) {
        void api.postMessagesReadV1(pollDetails.messageId);
      }
    }
  }, [api, isUnread, pollDetails?.messageId, pollId]);

  // Optimistically update UI
  if (pollDetails?.isUnread) {
    // oxlint-disable-next-line react-hooks/immutability
    pollDetails.isUnread = false;
  }

  const redirectState: MessageRouteStates["list"] = useMemo(() => ({ contentNotFoundReason: "not-found" }), []);

  // If poll is not found, redirect to message feed list with notification
  if (isHttpError(errorPollDetails) && errorPollDetails.status === 403) {
    return <Redirect to={routes.communityFeed.list} state={redirectState} />;
  }

  if (errorPollDetails) {
    return <ErrorPage error={errorPollDetails} />;
  }

  if (isPendingPollDetails) {
    return <FullSizeLoader />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={getCommunityPostDocumentTitle(pollDetails, t("page.poll-details.title"))}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.message-feed.title"),
              to: routes.communityFeed.list({
                slug,
              }),
            },
            {
              name: t("page.poll-details.title"),
            },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: [projectId] }}
          context={{ scope: "project", entity: "poll", page: "detail", entityId: pollDetails.id }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.poll-detail.suggestion.1"),
            t("component.ask-nobi-chat-drawer.poll-detail.suggestion.2"),
          ]}
        />
      }
      contentSize="constrained"
    >
      <CommunityPostViewCountTrackingProvider>
        <CommunityPoll poll={pollDetails} isExpanded />
      </CommunityPostViewCountTrackingProvider>
    </DocumentPage>
  );
}
